import { Box, Button, Stack, TextField } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Commentable } from 'Types/enums';

import { useFetchData } from '../../hooks/useFetchData';
import { createComment } from '../../services/comments';
import { StyledSkeleton } from '../../theme/CustomComponents';
import { CommentWithCreator } from '../../Types/comment';
import { entityCommentsPathUrl } from '../../util/routesUrls';
import { buildThreadedComments } from './buildThreadedComments';
import Comment from './Comment';
import CommentSkeleton from './CommentSkeleton';

export interface CommentsSectionProps {
  entityId: string;
  entityType: Commentable;
}
const Comments: React.FC<CommentsSectionProps> = ({ entityId, entityType }) => {
  const {
    data: comments,
    loading,
    refetch: refetchComments,
  } = useFetchData<CommentWithCreator[]>({
    urlPath: entityCommentsPathUrl(entityType, entityId),
  });
  const [newComment, setNewComment] = useState<string>('');
  const [newlyCreatedId, setNewlyCreatedId] = useState<string | null>(null);

  const threadedComments = useMemo(() => buildThreadedComments(comments ?? []), [comments]);

  const handleAddComment = useCallback(async () => {
    if (!newComment?.trim()) return;
    setNewComment('');
    const comment = await createComment({ entityId, content: newComment, entityType });
    await refetchComments();
    setNewlyCreatedId(comment.id);
  }, [entityId, entityType, newComment, refetchComments]);

  const onScrolled = useCallback(() => setNewlyCreatedId(null), []);

  const initialScroll = useRef(false);
  // This ensures that the newly created comment is scrolled when the component mounts
  useEffect(() => {
    if (initialScroll.current) return;

    if (threadedComments.length > 0) {
      const lastComment = threadedComments[threadedComments.length - 1];
      setNewlyCreatedId(lastComment.id);
      initialScroll.current = true;
    }
  }, [threadedComments]);

  return (
    <Box
      sx={{
        height: '100%',
        paddingX: 5,
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0, py: 2 }}>
        <Stack spacing={3}>
          {loading && !threadedComments.length
            ? [...Array(3)].map((_, idx) => <CommentSkeleton key={idx} />)
            : threadedComments.map((comment) => (
                <Comment
                  key={comment.id}
                  comment={comment}
                  entityId={entityId}
                  entityType={entityType}
                  autoScroll={newlyCreatedId === comment.id}
                  onScrolled={onScrolled}
                  refetchComments={refetchComments}
                />
              ))}
        </Stack>
      </Box>

      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          pt: 2,
          pb: 1,
        }}
      >
        {loading && !threadedComments.length ? (
          <StyledSkeleton variant="rectangular" height={96} sx={{ borderRadius: 1 }} />
        ) : (
          <TextField
            fullWidth
            multiline
            autoFocus
            minRows={2}
            maxRows={5}
            label="Add a comment..."
            variant="outlined"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                e.preventDefault();
                handleAddComment();
              }
            }}
          />
        )}
        <Box display="flex" justifyContent="flex-end" mt={1}>
          {loading && !threadedComments.length ? (
            <StyledSkeleton
              variant="rectangular"
              width={100}
              height={36}
              sx={{ borderRadius: 1 }}
            />
          ) : (
            <Button variant="contained" onClick={handleAddComment} disabled={!newComment?.trim()}>
              Comment
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Comments;
